import { useState, useEffect } from "react";
import { Save, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useTheme } from "../../contexts/ThemeContext";
import { ColorField, SectionHeader } from "./EditorFields";

const FONT_PRESETS = [
  {
    label: "Cabinet + Manrope (recommended)",
    value: '"Cabinet Grotesk", "Manrope", ui-sans-serif, system-ui, sans-serif',
  },
  { label: "Manrope", value: '"Manrope", ui-sans-serif, system-ui, sans-serif' },
  { label: "Inter", value: '"Inter", ui-sans-serif, system-ui, sans-serif' },
  { label: "Roboto", value: '"Roboto", ui-sans-serif, system-ui, sans-serif' },
  { label: "Montserrat", value: '"Montserrat", ui-sans-serif, system-ui, sans-serif' },
  { label: "JetBrains Mono", value: '"JetBrains Mono", ui-monospace, monospace' },
  { label: "System UI", value: "ui-sans-serif, system-ui, sans-serif" },
];

const FONT_FIELDS = [
  { key: "font_heading", label: "Заголовки" },
  { key: "font_body", label: "Основной текст" },
  { key: "font_menu", label: "Меню и навигация" },
  { key: "font_logo", label: "Логотип и подпись" },
  { key: "font_mono", label: "Служебный (overline и т.п.)" },
];

const OVERLAY_FIELDS = [
  { key: "hero_overlay_left", label: "Hero: левый край" },
  { key: "hero_overlay_mid", label: "Hero: центр слева" },
  { key: "hero_overlay_right", label: "Hero: правый край" },
  { key: "hero_overlay_bottom", label: "Hero: низ" },
  { key: "hero_overlay_center", label: "Hero: центр" },
];

const clampOpacity = (v) => Math.max(0, Math.min(1, Number(v) || 0));

export default function ThemeEditor() {
  const { theme, setTheme, applyTheme, refresh, DEFAULT_THEME } = useTheme();
  const [draft, setDraft] = useState(theme);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(theme);
  }, [theme]);

  const update = (key, value) => {
    const next = {
      ...draft,
      [key]: OVERLAY_FIELDS.some((f) => f.key === key) ? clampOpacity(value) : value,
    };
    setDraft(next);
    applyTheme(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/theme", draft);
      toast.success("Тема сохранена");
      setTheme(draft);
      await refresh();
    } catch {
      toast.error("Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Сбросить тему к значениям по умолчанию?")) return;
    setSaving(true);
    try {
      await api.post("/admin/theme/reset");
      setDraft(DEFAULT_THEME);
      applyTheme(DEFAULT_THEME);
      toast.success("Сброшено");
      await refresh();
    } catch {
      toast.error("Не удалось сбросить");
    } finally {
      setSaving(false);
    }
  };

  const colorFields = [
    ["midnight", "Midnight (главный фон)"],
    ["midnight_2", "Midnight-2 (вторичный фон)"],
    ["midnight_3", "Midnight-3 (поля / акценты)"],
    ["coral", "Coral (основной акцент)"],
    ["beige", "Beige (светлые элементы)"],
    ["gold", "Gold (запасной акцент)"],
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="bg-midnight-2 border border-white/10 p-6 md:p-8">
        <SectionHeader title="Тема сайта" />
        <p className="text-white/55 text-sm mb-6">
          Изменения применяются сразу. Нажмите «Сохранить», чтобы сохранить для всех.
        </p>

        <div className="mb-8">
          <SectionHeader title="Цвета бренда" />
          <div className="space-y-1">
            {colorFields.map(([k, label]) => (
              <ColorField key={k} label={label} value={draft[k]} onChange={(v) => update(k, v)} />
            ))}
          </div>
        </div>

        <div className="mb-8">
          <SectionHeader title="Прозрачность фона (главный экран)" />
          <div className="space-y-4">
            {OVERLAY_FIELDS.map(({ key, label }) => (
              <label key={key} className="block bg-midnight-3 border border-white/10 p-4">
                <div className="flex items-center justify-between gap-4 mb-2">
                  <span className="font-display text-white text-[14px]">{label}</span>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    value={draft[key] ?? 0}
                    onChange={(e) => update(key, e.target.value)}
                    className="w-20 bg-midnight border border-white/10 focus:border-coral px-2 py-1 text-white outline-none font-mono text-xs"
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={draft[key] ?? 0}
                  onChange={(e) => update(key, e.target.value)}
                  className="w-full accent-[rgb(var(--c-coral))]"
                />
              </label>
            ))}
          </div>
        </div>

        <div>
          <SectionHeader title="Шрифты" />
          <div className="space-y-4">
            {FONT_FIELDS.map(({ key, label }) => (
              <div key={key} className="bg-midnight-3 border border-white/10 p-4">
                <div className="overline mb-2">{label}</div>
                <select
                  value={FONT_PRESETS.some((p) => p.value === draft[key]) ? draft[key] : "__custom__"}
                  onChange={(e) => {
                    if (e.target.value !== "__custom__") update(key, e.target.value);
                  }}
                  className="w-full bg-midnight border border-white/10 focus:border-coral px-3 py-2 text-white outline-none font-display text-sm"
                >
                  {FONT_PRESETS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                  <option value="__custom__">Свой вариант (ниже)</option>
                </select>
                <input
                  type="text"
                  value={draft[key] || ""}
                  onChange={(e) => update(key, e.target.value)}
                  placeholder='"Inter", ui-sans-serif, system-ui, sans-serif'
                  className="mt-2 w-full bg-midnight border border-white/10 focus:border-coral px-3 py-2 text-white outline-none font-mono text-xs"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <button
            data-testid="theme-reset"
            onClick={reset}
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[11px] tracking-widest uppercase hover:border-brand-red hover:text-brand-red transition-colors"
          >
            <RotateCcw size={13} /> Сброс
          </button>
          <button
            data-testid="theme-save"
            onClick={save}
            disabled={saving}
            className="btn-primary !py-2 !px-5 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "..." : "Сохранить"}
          </button>
        </div>
      </div>

      <div className="bg-midnight-2 border border-white/10 p-6 md:p-8">
        <SectionHeader title="Превью шрифтов" />
        <div className="space-y-4">
          <div className="bg-midnight border border-white/10 p-6">
            <div className="overline mb-2">Пример · Заголовок</div>
            <h3 className="font-display font-black text-white text-2xl uppercase mb-2">
              Бизнес-тур в Китай
            </h3>
            <p className="text-white/65 text-sm mb-4">
              Пример основного текста для проверки выбранной типографики.
            </p>
            <div className="font-menu text-white/80 text-sm uppercase tracking-[0.15em] mb-3">
              Главная · Бизнес · Контакты
            </div>
            <div className="font-logo text-white text-xl font-bold mb-4">DIYATOUR</div>
            <div className="font-mono text-coral text-xs tracking-[0.2em] uppercase">
              Mono / Overline / Service text
            </div>
          </div>

          <div className="bg-midnight-2 border border-white/10 p-6">
            <div className="grid grid-cols-3 gap-3">
              {colorFields.map(([k]) => (
                <div key={k} className="text-center">
                  <div className="w-full aspect-square mb-2 border border-white/10" style={{ background: draft[k] }} />
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/55">{k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
